'use client';

import type { ReactNode } from 'react';
import { useSyncExternalStore } from 'react';

import { cn } from '../../cn';

/**
 * How far the page has to move before the bar draws its own ground. Not zero: a
 * rubber-band scroll on a trackpad crosses 0 constantly, and the ground flickers
 * with it.
 */
const GROUND_AT = 8;

/* Module scope, so both stay referentially stable across renders. */
const subscribe = (onChange: () => void) => {
  window.addEventListener('scroll', onChange, { passive: true });
  return () => window.removeEventListener('scroll', onChange);
};
const getSnapshot = () => window.scrollY > GROUND_AT;
/* The server has no scroll position, and the top of the page is the safe guess. */
const getServerSnapshot = () => false;

/**
 * The bar, and the one thing about it that needs the browser.
 *
 * Split out so the header itself stays on the server. A `'use client'` takes a
 * component's whole import tree with it, so one scroll boolean on SiteHeader
 * sent the wordmark, the container and two icons to the client too. Children
 * arrive already rendered, because a component type cannot cross that boundary.
 */
export const StickyHeader = ({ children }: { children: ReactNode }) => {
  /*
   * At the top of a page the bar takes no ground of its own. The hero's wash
   * rises behind the nav, and a flat colour painted over a gradient matches at
   * one point and shows as a band everywhere else.
   *
   * useSyncExternalStore rather than an effect that seeds its own state. A
   * reload can restore a scroll position before an effect ever runs, and
   * seeding from inside one renders the wrong bar first and corrects it.
   */
  const scrolled = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  /* While the mobile panel is open the bar takes the panel's slate-50, scrolled
     or not, so bar and panel read as one sheet. :has() rather than state: the
     panel's open flag lives in MobileNav, which arrives here already rendered.
     The fade's colour follows, or its white gradient would paint over it. */
  return (
    <header
      className={cn(
        'header-fade sticky top-0 z-50',
        !scrolled && '[--fade-strength:0]',
        'has-[[data-menu-open]]:bg-slate-50 has-[[data-menu-open]]:[--fade-color:var(--color-slate-50)] has-[[data-menu-open]]:[--fade-strength:1]',
      )}>
      {children}
    </header>
  );
};
